import {
  Alert,
  Box,
  Button,
  Group,
  Loader,
  Radio,
  Stack,
  Switch,
  Text,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { SCHEDULE_FREQUENCY } from 'api/types/card-schedule.types';
import { useCompanyFeatureFlags } from 'hooks/use-company-feature-flags';
import { SourceFilterButton } from 'modules/shared/libs/chat/components/SourceFilterButton';
import TEST_IDS from 'test-ids';

import CardSchedulePrompt from './CardSchedulePrompt';

import type { ScheduleFrequency } from 'api/types/card-schedule.types';
import type { Source } from 'modules/shared/api/sources.api.types';
import type { ReactNode } from 'react';

type CardScheduleContentProps = {
  prompt: string;
  onPromptChange: (prompt: string) => void;
  frequency: ScheduleFrequency;
  onFrequencyChange: (frequency: ScheduleFrequency) => void;
  researchMode: boolean;
  onResearchModeChange: (enabled: boolean) => void;
  disabledSources: string[];
  onDisabledSourcesChange: (sources: string[]) => void;
  // When sources is a non-empty list, the SourceFilterButton is shown.
  // Pass `undefined` or `[]` (e.g., a still-loading/errored query) and
  // the button is kept mounted but hidden via `visibility: hidden` — the
  // surrounding Group still reserves height via the disabled research-mode
  // Switch.
  sources?: Source[];
  isLoadingSources?: boolean;
  isGeneratingPrompt?: boolean;
  promptGenerationError?: boolean;
  onRetryGeneration?: () => void;
  onCancelGeneration?: () => void;
};

const CardScheduleContent = ({
  prompt,
  onPromptChange,
  frequency,
  onFrequencyChange,
  researchMode,
  onResearchModeChange,
  disabledSources,
  onDisabledSourcesChange,
  sources,
  isLoadingSources = false,
  isGeneratingPrompt = false,
  promptGenerationError = false,
  onRetryGeneration,
  onCancelGeneration,
}: CardScheduleContentProps): ReactNode => {
  const { t } = useTranslation(['Card', 'Common']);
  const { isDailyCardScheduleEnabled } = useCompanyFeatureFlags(
    'isDailyCardScheduleEnabled',
  );

  return (
    <Stack gap="md" flex={1} mih={0}>
      <Group gap="xs" align="center">
        <Text fw={600} fz="lg">
          {t('Card:schedule.prompt.label')}
        </Text>
        {isGeneratingPrompt && <Loader size="xs" />}
      </Group>

      <Stack gap={0} flex={1} mih={120}>
        {!promptGenerationError && (
          <CardSchedulePrompt
            prompt={prompt}
            onChange={onPromptChange}
            disabled={isGeneratingPrompt}
            placeholder={
              isGeneratingPrompt && !prompt
                ? t('Card:schedule.prompt.generating')
                : undefined
            }
          />
        )}

        {promptGenerationError && (
          <Alert color="red" title={t('Card:schedule.prompt.generateError')}>
            <Group gap="xs">
              <Button
                size="xs"
                variant="subtle"
                color="gray"
                onClick={onCancelGeneration}
              >
                {t('Common:actions.cancel')}
              </Button>
              <Button size="xs" variant="light" onClick={onRetryGeneration}>
                {t('Card:schedule.prompt.tryAgain')}
              </Button>
            </Group>
          </Alert>
        )}
      </Stack>

      <Group gap="md" align="center" mt="md">
        <Switch
          label={t('Card:schedule.researchMode.label')}
          checked={researchMode}
          disabled
          onChange={(event) =>
            onResearchModeChange(event.currentTarget.checked)
          }
          data-test-id={TEST_IDS.cardSchedule.researchModeToggle}
        />

        <Box
          style={{
            visibility: (sources?.length ?? 0) > 0 ? 'visible' : 'hidden',
          }}
        >
          <SourceFilterButton
            sources={sources ?? []}
            disabledSources={disabledSources}
            onDisabledSourcesChange={onDisabledSourcesChange}
            isLoading={isLoadingSources}
          />
        </Box>
      </Group>

      <Text fw={600} fz="lg" mt="lg">
        {t('Card:schedule.radioLabel')}
      </Text>

      <Radio.Group
        value={frequency}
        onChange={(value) => onFrequencyChange(value as ScheduleFrequency)}
        data-test-id={TEST_IDS.cardSchedule.radioGroup}
      >
        <Stack gap="md">
          <Radio
            value={SCHEDULE_FREQUENCY.UNSCHEDULED}
            label={t('Card:schedule.frequency.unscheduled')}
          />
          {(isDailyCardScheduleEnabled ||
            frequency === SCHEDULE_FREQUENCY.DAILY) && (
            <Radio
              value={SCHEDULE_FREQUENCY.DAILY}
              label={t('Card:schedule.frequency.daily')}
              disabled={!isDailyCardScheduleEnabled}
            />
          )}
          <Radio
            value={SCHEDULE_FREQUENCY.WEEKLY}
            label={t('Card:schedule.frequency.weekly')}
          />
          <Radio
            value={SCHEDULE_FREQUENCY.BIWEEKLY}
            label={t('Card:schedule.frequency.biweekly')}
          />
          <Radio
            value={SCHEDULE_FREQUENCY.MONTHLY}
            label={t('Card:schedule.frequency.monthly')}
          />
          <Radio
            value={SCHEDULE_FREQUENCY.QUARTERLY}
            label={t('Card:schedule.frequency.quarterly')}
          />
        </Stack>
      </Radio.Group>
    </Stack>
  );
};

export default CardScheduleContent;
